import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Square, Loader2, Sparkles, X, Type, Send } from "lucide-react";
import IagoSpinner from "../iago/IagoSpinner";
import IagoAvatar from "../iago/IagoAvatar";
import clsx from "clsx";
import { api } from "../../lib/api";
import { useStore } from "../../stores/store";

type Stage = "idle" | "recording" | "transcribing" | "generating" | "preview" | "error";

interface Props {
  open: boolean;
  onClose: () => void;
  defaultLaneId: number | null;
}

export function VoiceRecorder({ open, onClose, defaultLaneId }: Props) {
  const [stage, setStage] = useState<Stage>("idle");
  const [transcript, setTranscript] = useState("");
  const [manualText, setManualText] = useState("");
  const [typedMode, setTypedMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [levels, setLevels] = useState<number[]>(new Array(48).fill(0));
  const [peak, setPeak] = useState(0);

  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);

  const upsertCard = useStore((s) => s.upsertCard);
  const markFreshCard = useStore((s) => s.markFreshCard);
  const setActiveCard = useStore((s) => s.setActiveCard);

  function reset() {
    setStage("idle");
    setTranscript("");
    setManualText("");
    setError(null);
    setProvider(null);
    setElapsed(0);
    setLevels(new Array(48).fill(0));
    setPeak(0);
    setTypedMode(false);
    cleanupMedia();
  }

  function cleanupMedia() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) window.clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
    analyserRef.current = null;
    mediaRef.current = null;
    chunksRef.current = [];
  }

  useEffect(() => {
    if (!open) reset();
    return () => cleanupMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function startRecording() {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.75;
      source.connect(analyser);
      analyserRef.current = analyser;

      const mr = new MediaRecorder(stream, { mimeType: pickMime() });
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.start();
      setStage("recording");

      setElapsed(0);
      timerRef.current = window.setInterval(() => setElapsed((v) => v + 1), 1000);

      const loop = () => {
        if (!analyserRef.current) return;
        const data = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(data);
        const bins = 48;
        const step = Math.max(1, Math.floor(data.length / bins));
        const arr: number[] = [];
        let sumAll = 0;
        for (let i = 0; i < bins; i++) {
          let sum = 0;
          for (let j = 0; j < step; j++) sum += data[i * step + j];
          const v = sum / step / 255;
          arr.push(v);
          sumAll += v;
        }
        setLevels(arr);
        setPeak(sumAll / bins);
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    } catch (e: any) {
      setError("Não foi possível acessar o microfone: " + e.message);
      setStage("error");
    }
  }

  function pickMime(): string {
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
    for (const c of candidates) {
      if (MediaRecorder.isTypeSupported(c)) return c;
    }
    return "";
  }

  async function stopRecording() {
    const mr = mediaRef.current;
    if (!mr) return;
    await new Promise<void>((res) => {
      mr.onstop = () => res();
      mr.stop();
    });

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) window.clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());

    const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
    if (blob.size < 1000) {
      setError("Áudio muito curto. Tente novamente.");
      setStage("error");
      return;
    }

    setStage("transcribing");
    try {
      const { text } = await api.transcribe(blob);
      setTranscript(text);
      if (!text) {
        setError("Nenhuma fala detectada.");
        setStage("error");
        return;
      }
      await runGeneration(text);
    } catch (e: any) {
      setError(e.message);
      setStage("error");
    }
  }

  async function runGeneration(text: string) {
    setStage("generating");
    try {
      const { card, provider: p } = await api.generateCard(text, defaultLaneId);
      setProvider(p);
      upsertCard(card);
      markFreshCard(card.id);
      setStage("preview");
      setTimeout(() => {
        setActiveCard(card.id);
        onClose();
      }, 900);
    } catch (e: any) {
      setError(e.message);
      setStage("error");
    }
  }

  async function submitTyped() {
    if (!manualText.trim()) return;
    setTranscript(manualText);
    await runGeneration(manualText);
  }

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      if (e.key === " " && stage === "idle" && !typedMode) {
        e.preventDefault();
        startRecording();
      }
      if (e.key === " " && stage === "recording") {
        e.preventDefault();
        stopRecording();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, stage, typedMode]);

  const stageTitle: Record<Stage, string> = {
    idle: typedMode ? "Digite sua ideia" : "O que você precisa fazer?",
    recording: "Estou ouvindo",
    transcribing: "Transformando em palavras",
    generating: "Dando estrutura à ideia",
    preview: "Pronto",
    error: "Algo deu errado",
  };

  const stageHint: Record<Stage, string> = {
    idle: typedMode ? "Cmd+Enter pra gerar" : "Fale como se contasse pra um colega",
    recording: "quando terminar, aperte Espaço ou o botão",
    transcribing: "Whisper Turbo · Groq",
    generating: "IA organizando os detalhes",
    preview: "",
    error: "",
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/70 backdrop-blur-md"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Ambient glow radial que pulsa durante recording */}
          <motion.div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            animate={{
              opacity: stage === "recording" ? [0.5, 0.9, 0.5] : stage === "generating" ? 0.6 : 0.35,
            }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            style={{
              background:
                stage === "recording"
                  ? "radial-gradient(500px 500px at 50% 45%, rgba(239,68,68,0.22), transparent 60%)"
                  : stage === "generating"
                    ? "radial-gradient(600px 600px at 50% 45%, rgba(168,85,247,0.25), transparent 60%)"
                    : stage === "preview"
                      ? "radial-gradient(600px 600px at 50% 45%, rgba(16,185,129,0.22), transparent 60%)"
                      : "radial-gradient(500px 500px at 50% 45%, rgba(168,85,247,0.12), transparent 60%)",
            }}
          />

          <motion.div
            className="glass relative w-full max-w-none overflow-hidden rounded-none p-5 shadow-elevated md:w-[600px] md:max-w-[94vw] md:rounded-[26px] md:p-8"
            initial={{ scale: 0.9, y: 14, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, y: 10, opacity: 0 }}
            transition={{ type: "spring", damping: 22, stiffness: 240 }}
          >
            {/* Mesh bg animado */}
            <div className="pointer-events-none absolute inset-0 mesh-drift opacity-60" />
            {/* Linha top luminosa */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/60 to-transparent" />

            <button
              onClick={onClose}
              className="absolute right-4 top-4 z-10 rounded-lg p-1.5 text-slate-400 transition hover:bg-white/5 hover:text-slate-100"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Header com transições de título */}
            <div className="relative flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.18em] text-violet-300/90">
              <motion.span
                key={`ic-${stage}`}
                initial={{ scale: 0.6, opacity: 0, rotate: -12 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                transition={{ type: "spring", damping: 14, stiffness: 220 }}
                className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br from-violet-500/40 to-fuchsia-500/40 ring-1 ring-violet-400/40"
              >
                <Sparkles className="h-3 w-3" />
              </motion.span>
              Nova tarefa via IA
              {(stage === "transcribing" || stage === "generating") && (
                <span className="ml-2 inline-flex items-center gap-0.5">
                  <span className="typing-dot inline-block h-1 w-1 rounded-full bg-violet-300" />
                  <span className="typing-dot inline-block h-1 w-1 rounded-full bg-violet-300" />
                  <span className="typing-dot inline-block h-1 w-1 rounded-full bg-violet-300" />
                </span>
              )}
            </div>

            <div className="relative mt-1 h-9 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.h2
                  key={stageTitle[stage] + (typedMode ? "-t" : "")}
                  initial={{ y: 14, opacity: 0, filter: "blur(6px)" }}
                  animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
                  exit={{ y: -12, opacity: 0, filter: "blur(6px)" }}
                  transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
                  className="absolute inset-x-0 text-2xl font-semibold tracking-tight text-slate-100"
                >
                  {stageTitle[stage]}
                </motion.h2>
              </AnimatePresence>
            </div>
            <div className="relative h-4">
              <AnimatePresence mode="wait">
                {stageHint[stage] && (
                  <motion.p
                    key={`h-${stage}-${typedMode ? "t" : "v"}`}
                    initial={{ y: 6, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -4, opacity: 0 }}
                    transition={{ duration: 0.22 }}
                    className="absolute inset-x-0 text-[11px] text-slate-500"
                  >
                    {stageHint[stage]}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            <div className="relative mt-6 flex flex-col items-center">
              {/* ─────── HERO NUCLEUS ─────── */}
              {stage !== "idle" || !typedMode ? (
                <div className="relative flex h-64 w-full items-center justify-center">
                  <Nucleus
                    stage={stage}
                    levels={levels}
                    peak={peak}
                    onStart={startRecording}
                    onStop={stopRecording}
                  />
                </div>
              ) : null}

              {/* ─────── CONTEÚDO POR STAGE ─────── */}
              <div className="relative mt-2 w-full">
                <AnimatePresence mode="wait">
                  {stage === "idle" && !typedMode && (
                    <motion.div
                      key="idle-voice-foot"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.25 }}
                      className="flex flex-col items-center gap-3 pt-2"
                    >
                      <p className="text-center text-[13px] text-slate-400">
                        Clique ou pressione{" "}
                        <kbd className="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 font-mono text-[11px] text-slate-200">
                          Espaço
                        </kbd>{" "}
                        para começar
                      </p>
                      <button
                        onClick={() => setTypedMode(true)}
                        className="group flex items-center gap-1.5 text-xs text-slate-500 transition hover:text-slate-200"
                      >
                        <Type className="h-3 w-3 transition group-hover:scale-110" /> ou digite a ideia
                      </button>
                    </motion.div>
                  )}

                  {stage === "idle" && typedMode && (
                    <motion.div
                      key="idle-typed"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.25 }}
                      className="flex flex-col gap-3 pt-4"
                    >
                      <textarea
                        autoFocus
                        value={manualText}
                        onChange={(e) => setManualText(e.target.value)}
                        onKeyDown={(e) => {
                          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                            e.preventDefault();
                            submitTyped();
                          }
                        }}
                        placeholder="Descreva como se estivesse falando. Ex: 'comprar presente da Maria até sexta' ou 'estudar capítulo 3 do livro de história amanhã de manhã'"
                        className="min-h-[150px] w-full resize-none rounded-xl border border-white/10 bg-black/30 p-4 text-sm leading-relaxed text-slate-100 shadow-inner shadow-black/20 placeholder:text-slate-500 transition focus:border-violet-500/60 focus:outline-none focus:shadow-[0_0_0_3px_rgba(168,85,247,0.12)]"
                      />
                      <div className="flex items-center justify-between gap-2">
                        <button
                          onClick={() => setTypedMode(false)}
                          className="group flex items-center gap-1.5 text-xs text-slate-500 transition hover:text-slate-200"
                        >
                          <Mic className="h-3 w-3 transition group-hover:scale-110" /> usar áudio
                        </button>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={submitTyped}
                          disabled={!manualText.trim()}
                          className="group relative flex items-center gap-1.5 overflow-hidden rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 px-3.5 py-2 text-sm font-medium text-white shadow-[0_0_20px_rgba(168,85,247,0.4)] transition disabled:opacity-40"
                        >
                          <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                          <Send className="relative h-3.5 w-3.5" />
                          <span className="relative">Gerar tarefa</span>
                        </motion.button>
                      </div>
                    </motion.div>
                  )}

                  {stage === "recording" && (
                    <motion.div
                      key="rec-foot"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.25 }}
                      className="flex flex-col items-center gap-2 pt-1"
                    >
                      <div className="flex items-center gap-3 font-mono text-sm">
                        <span className="flex items-center gap-1.5 text-fuchsia-300">
                          <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-fuchsia-400/70" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-fuchsia-400" />
                          </span>
                          gravando
                        </span>
                        <span className="text-slate-300 tabular-nums">
                          {String(Math.floor(elapsed / 60)).padStart(2, "0")}:
                          {String(elapsed % 60).padStart(2, "0")}
                        </span>
                      </div>
                    </motion.div>
                  )}

                  {stage === "transcribing" && (
                    <motion.div
                      key="trans-foot"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.25 }}
                      className="flex flex-col items-center gap-1 pt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-300/80"
                    >
                      <div className="flex items-center gap-1.5">
                        <IagoSpinner size={14} /> transcrevendo
                      </div>
                    </motion.div>
                  )}

                  {stage === "generating" && (
                    <motion.div
                      key="gen-foot"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.25 }}
                      className="flex flex-col items-center gap-2 pt-1"
                    >
                      {transcript && (
                        <motion.p
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.15 }}
                          className="max-w-[460px] text-center text-xs italic leading-relaxed text-slate-500"
                        >
                          “{transcript.slice(0, 200)}{transcript.length > 200 ? "…" : ""}”
                        </motion.p>
                      )}
                      <div className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-violet-300/80">
                        <IagoSpinner size={14} />
                        estruturando o card
                      </div>
                    </motion.div>
                  )}

                  {stage === "preview" && (
                    <motion.div
                      key="prev-foot"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.25, delay: 0.1 }}
                      className="flex flex-col items-center gap-1 pt-1 text-center"
                    >
                      <motion.p
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-sm font-medium text-slate-100"
                      >
                        Tarefa criada
                      </motion.p>
                      {provider && (
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.35 }}
                          className="font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-300/70"
                        >
                          via {provider}
                        </motion.p>
                      )}
                    </motion.div>
                  )}

                  {stage === "error" && (
                    <motion.div
                      key="err-foot"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: [0, -6, 6, -4, 4, 0] }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="flex flex-col items-center gap-3 py-2"
                    >
                      <div className="max-w-[440px] rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-center text-sm text-rose-200 shadow-[0_0_24px_rgba(239,68,68,0.2)]">
                        {error}
                      </div>
                      <button
                        onClick={reset}
                        className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm text-slate-100 transition hover:border-white/20 hover:bg-white/10"
                      >
                        Tentar novamente
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ────────────────── NUCLEUS — o herói único que se transforma ──────────────────

interface NucleusProps {
  stage: Stage;
  levels: number[];
  peak: number;
  onStart: () => void;
  onStop: () => void;
}

function Nucleus({ stage, levels, peak, onStart, onStop }: NucleusProps) {
  const STAGE_COLORS: Record<Stage, { a: string; b: string; c: string }> = {
    idle:         { a: "#a855f7", b: "#ec4899", c: "#38bdf8" },
    recording:    { a: "#8b5cf6", b: "#d946ef", c: "#0ea5e9" },
    transcribing: { a: "#22d3ee", b: "#38bdf8", c: "#a855f7" },
    generating:   { a: "#a855f7", b: "#d946ef", c: "#38bdf8" },
    preview:      { a: "#10b981", b: "#14b8a6", c: "#22d3ee" },
    error:        { a: "#f43f5e", b: "#ef4444", c: "#f97316" },
  };
  const c = STAGE_COLORS[stage];

  // intensidade do áudio (0..1), boostada pro visual feedback ficar evidente
  const intensity = Math.min(1, Math.max(peak * 2.2, 0));
  const coreScale = 1 + intensity * 0.14;
  const glowAlpha = 0.55 + intensity * 0.4;
  const haloScale = 1.6 + intensity * 0.4;

  return (
    <div className="relative flex h-64 w-64 items-center justify-center">
      {/* Halo dinâmico — expande com áudio */}
      <motion.div
        aria-hidden
        className="absolute inset-0 rounded-full"
        animate={{
          scale: stage === "recording" ? haloScale : stage === "generating" ? [1.2, 1.35, 1.2] : 1.1,
          opacity: stage === "recording" ? 0.55 : stage === "generating" ? 0.45 : 0.3,
        }}
        transition={
          stage === "recording"
            ? { duration: 0.08, ease: "linear" }
            : { duration: 2.4, repeat: Infinity, ease: "easeInOut" }
        }
        style={{
          background: `radial-gradient(circle at 50% 50%, ${c.a}60 0%, ${c.b}30 40%, transparent 70%)`,
          filter: "blur(16px)",
        }}
      />

      {/* Anel de gradiente cônico girando (muda de cor por stage) */}
      <motion.div
        aria-hidden
        className="conic-spin-slow absolute h-56 w-56 rounded-full"
        animate={{ opacity: stage === "preview" ? 0 : 1 }}
        transition={{ duration: 0.3 }}
        style={{
          background: `conic-gradient(from 0deg, ${c.a}, ${c.b}, ${c.c}, ${c.a})`,
          maskImage: "radial-gradient(circle, transparent 46%, black 47%, black 50%, transparent 52%)",
          WebkitMaskImage: "radial-gradient(circle, transparent 46%, black 47%, black 50%, transparent 52%)",
          filter: `blur(${stage === "idle" ? 0.5 : 0}px)`,
        }}
      />

      {/* Pulsos concêntricos durante recording. Três anéis que expandem
          proporcionalmente ao volume captado pelo microfone. */}
      {stage === "recording" && (
        <>
          {[0, 1, 2].map((ring) => {
            const slice = Math.floor(levels.length / 3);
            const avg =
              levels.slice(ring * slice, (ring + 1) * slice).reduce((a, b) => a + b, 0) /
              (slice || 1);
            const baseScale = 1.15 + ring * 0.22;
            const scale = baseScale + avg * 0.35;
            const opacity = 0.6 - ring * 0.15 - avg * 0.1;
            return (
              <motion.div
                key={ring}
                className="absolute rounded-full border"
                style={{
                  width: 112,
                  height: 112,
                  borderColor: ring === 0 ? c.a : ring === 1 ? c.b : c.c,
                  boxShadow: `0 0 ${12 + avg * 20}px ${ring === 0 ? c.a : ring === 1 ? c.b : c.c}66`,
                }}
                animate={{ scale, opacity: Math.max(0.08, opacity) }}
                transition={{ type: "spring", damping: 18, stiffness: 300 }}
              />
            );
          })}
        </>
      )}

      {/* Transcribing e generating: IagoAvatar no core + anel cônico já bastam. */}

      {/* Core — o centro de tudo, persistente */}
      <motion.button
        layoutId="nucleus-core"
        onClick={stage === "idle" ? onStart : stage === "recording" ? onStop : undefined}
        disabled={stage !== "idle" && stage !== "recording"}
        whileHover={stage === "idle" || stage === "recording" ? { scale: coreScale * 1.04 } : {}}
        whileTap={stage === "idle" || stage === "recording" ? { scale: 0.96 } : {}}
        animate={{
          scale: coreScale,
          width: stage === "preview" ? 88 : 112,
          height: stage === "preview" ? 88 : 112,
        }}
        transition={{ type: "spring", damping: 20, stiffness: 260 }}
        className={clsx(
          "group relative flex items-center justify-center rounded-full text-white",
          (stage === "idle" || stage === "recording") && "cursor-pointer",
          stage !== "idle" && stage !== "recording" && "cursor-default",
        )}
        style={{
          background: `radial-gradient(circle at 30% 30%, ${c.b}ee, ${c.a}cc 40%, ${c.c}aa 100%)`,
          boxShadow: `0 0 ${40 + intensity * 60}px ${c.a}${Math.round(glowAlpha * 255).toString(16).padStart(2, "0")}, 0 0 120px ${c.b}40, inset 0 2px 12px rgba(255,255,255,0.15), inset 0 -4px 16px rgba(0,0,0,0.3)`,
        }}
      >
        {/* highlight bevel */}
        <span className="pointer-events-none absolute inset-[3px] rounded-full bg-gradient-to-br from-white/25 via-transparent to-black/25" />

        <AnimatePresence mode="wait">
          {stage === "idle" && (
            <motion.span
              key="icon-mic"
              initial={{ scale: 0, rotate: -30, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              exit={{ scale: 0, rotate: 30, opacity: 0 }}
              transition={{ type: "spring", damping: 16 }}
              className="relative"
            >
              <Mic className="h-9 w-9 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]" />
            </motion.span>
          )}
          {stage === "recording" && (
            <motion.span
              key="icon-stop"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: "spring", damping: 16 }}
              className="relative flex h-7 w-7 items-center justify-center rounded-md bg-white/95"
              style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }}
            >
              <span className="block h-6 w-6 rounded-sm bg-gradient-to-br from-violet-500 to-fuchsia-500" />
            </motion.span>
          )}
          {stage === "transcribing" && (
            <motion.span
              key="icon-trans"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: "spring", damping: 16 }}
              className="relative"
            >
              <IagoAvatar size={56} state="thinking" float={false} />
            </motion.span>
          )}
          {stage === "generating" && (
            <motion.span
              key="icon-gen"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: "spring", damping: 16 }}
              className="relative"
            >
              <IagoAvatar size={56} state="thinking" float={false} />
            </motion.span>
          )}
          {stage === "preview" && (
            <motion.span
              key="icon-check"
              initial={{ scale: 0, rotate: -90 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0 }}
              transition={{ type: "spring", damping: 14, stiffness: 220 }}
              className="relative"
            >
              <svg viewBox="0 0 24 24" className="h-9 w-9" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <motion.path
                  d="M4 12.5l5 5L20 6"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.55, delay: 0.1, ease: [0.2, 0.8, 0.2, 1] }}
                />
              </svg>
            </motion.span>
          )}
          {stage === "error" && (
            <motion.span
              key="icon-err"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="relative"
            >
              <X className="h-9 w-9" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Partículas confetti só no preview */}
      {stage === "preview" &&
        Array.from({ length: 14 }).map((_, i) => {
          const angle = (i / 14) * Math.PI * 2;
          const fx = `${Math.cos(angle) * 60}px`;
          const fy = `${Math.sin(angle) * 60}px`;
          const colors = ["#10b981", "#14b8a6", "#22d3ee", "#a855f7", "#ec4899"];
          const color = colors[i % colors.length];
          return (
            <motion.span
              key={i}
              className="pointer-events-none absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-full"
              initial={{ x: -3, y: -3, scale: 1, opacity: 1 }}
              animate={{
                x: `calc(${fx} - 3px)`,
                y: `calc(${fy} - 3px)`,
                scale: 0,
                opacity: 0,
              }}
              transition={{ duration: 0.9, delay: 0.15 + i * 0.02, ease: "easeOut" }}
              style={{ backgroundColor: color, boxShadow: `0 0 10px ${color}` }}
            />
          );
        })}
    </div>
  );
}
